import React, { useState } from "react";
import PropTypes from "prop-types";
import axios from "axios";
import { Alert, Button, TextInput } from "@opendate-app/setlist";
import { toastSuccess } from "../../../shared/toastHelper";

// O-7265. A show in no recurring series sets its own pass holder limit here: one
// limit for the date, shared by every collection pass that covers it. The pool
// group stays locked against drag; this is the one place its size changes.
const PassHolderLimitField = ({ csrfToken, team, confirm, confirmChanged, ticketGroupChanged }) => {
  const settings = confirm.pass_holder_limit_settings;
  const initial = settings?.limit === null || settings?.limit === undefined ? "" : String(settings.limit);
  const [value, setValue] = useState(initial);
  const [saved, setSaved] = useState(initial);
  const [errors, setErrors] = useState(null);
  const [saving, setSaving] = useState(false);

  if (!settings) return null;

  const save = async () => {
    setSaving(true);
    setErrors(null);
    // The Tickets screen sets the token on axios' common headers; a second,
    // differently-cased header would be joined with it and fail the CSRF check.
    axios.defaults.headers.common["X-CSRF-TOKEN"] = csrfToken;
    try {
      const { data } = await axios.patch(
        `/teams/${team.id}/confirms/${confirm.id}/pass_holder_limit.json`,
        { pass_holder_limit: value === "" ? null : value },
      );
      if (data.root_ticket_group) ticketGroupChanged(data.root_ticket_group);
      // Keep the store's confirm current, so the field reopens on the saved limit.
      confirmChanged({ ...confirm, pass_holder_limit_settings: { ...settings, limit: data.pass_holder_limit } });
      const next = data.pass_holder_limit === null ? "" : String(data.pass_holder_limit);
      setValue(next);
      setSaved(next);
      toastSuccess("Pass holder limit saved.");
    } catch (e) {
      const apiErrors = e?.response?.data?.errors;
      setErrors(Array.isArray(apiErrors) && apiErrors.length > 0 ? apiErrors : ["Could not save the pass holder limit. Please try again."]);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="card mt-3" data-testid="event-pass-holder-limit">
      <div className="card-body">
        <div className="d-flex align-items-end justify-content-between flex-wrap" style={{ gap: 16 }}>
          <div>
            <div className="font-weight-bold">Pass holders per date</div>
            <div className="text-muted small">
              Tickets held on this date for pass and pack holders. One limit, shared by every collection pass that covers it.
            </div>
          </div>
          <div className="d-flex align-items-end" style={{ gap: 8 }}>
            <div style={{ width: 160 }}>
              <TextInput
                size="sm"
                type="number"
                inputMode="numeric"
                min={0}
                step="1"
                onWheel={(e) => e.currentTarget.blur()}
                id="event-pass-holder-limit-input"
                data-testid="event-pass-holder-limit-input"
                aria-label="Pass holders per date"
                placeholder="No limit"
                value={value}
                onChange={(e) => setValue(e.target.value)}
              />
            </div>
            <Button
              variant="secondary"
              data-testid="event-pass-holder-limit-save"
              disabled={saving || value === saved}
              onClick={save}
            >
              Save
            </Button>
          </div>
        </div>
        {errors && (
          <div className="mt-3" data-testid="event-pass-holder-limit-errors">
            <Alert color="error" role="alert" title="Not saved" description={errors.join(" ")} />
          </div>
        )}
      </div>
    </div>
  );
};

PassHolderLimitField.propTypes = {
  csrfToken: PropTypes.string,
  team: PropTypes.shape({ id: PropTypes.number.isRequired }).isRequired,
  confirm: PropTypes.shape({
    id: PropTypes.number.isRequired,
    pass_holder_limit_settings: PropTypes.shape({ limit: PropTypes.number }),
  }).isRequired,
  confirmChanged: PropTypes.func.isRequired,
  ticketGroupChanged: PropTypes.func.isRequired,
};

export default PassHolderLimitField;
